
import { useCallback } from 'react';

interface TrackConversionParams {
  event_name: string;
  user_email?: string;
  user_phone?: string;
  value?: number;
  currency?: string;
  content_name?: string;
  content_category?: string;
}

// Declare Facebook Pixel types for TypeScript
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

export const useMetaTracking = () => {
  // Get Facebook tracking parameters from cookies/localStorage
  const getFacebookParams = () => {
    try {
      // Get fbp (Facebook Browser ID) from cookie
      const fbp = document.cookie
        .split('; ')
        .find(row => row.startsWith('_fbp='))
        ?.split('=')[1];

      // Get fbc (Facebook Click ID) from URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      let fbc = urlParams.get('fbclid');
      
      if (fbc) {
        // Store in localStorage for future use
        localStorage.setItem('_fbc', `fb.1.${Date.now()}.${fbc}`);
        fbc = `fb.1.${Date.now()}.${fbc}`;
      } else {
        // Try to get from localStorage
        fbc = localStorage.getItem('_fbc') || undefined;
      }

      return { fbp, fbc };
    } catch (error) {
      console.warn('Could not get Facebook tracking parameters:', error);
      return { fbp: undefined, fbc: undefined };
    }
  };

  // Fire client-side Facebook Pixel event
  const fireClientSideEvent = (eventName: string, params: any = {}) => {
    try {
      if (typeof window !== 'undefined' && window.fbq) {
        console.log('Firing client-side Facebook Pixel event:', eventName, params);
        window.fbq('track', eventName, params);
      } else {
        console.warn('Facebook Pixel not available for client-side tracking');
      }
    } catch (error) {
      console.error('Client-side Facebook Pixel error:', error);
    }
  };

  const trackConversion = useCallback(async (params: TrackConversionParams) => {
    try {
      console.log('Attempting to track Meta conversion:', params);
      
      const { fbp, fbc } = getFacebookParams();
      
      // Prepare client-side event parameters
      const clientParams: any = {};
      if (params.value) {
        clientParams.value = params.value;
        clientParams.currency = params.currency || 'USD';
      }
      if (params.content_name) {
        clientParams.content_name = params.content_name;
      }
      if (params.content_category) {
        clientParams.content_category = params.content_category;
      }
      
      // Fire client-side Facebook Pixel event
      fireClientSideEvent(params.event_name, clientParams);
      
      // Call the Supabase edge function for server-side tracking
      const response = await fetch('/api/meta-conversion-tracking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...params,
          event_source_url: window.location.href,
          fbp,
          fbc,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Meta conversion tracked successfully (server-side):', result);
        return { success: true, data: result };
      } else {
        console.error('Meta tracking failed (server-side):', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Meta tracking error:', error);
      return { success: false, error };
    }
  }, []);

  // Predefined tracking functions for common events
  const trackPurchase = useCallback((params: { 
    user_email?: string; 
    value: number; 
    currency?: string;
    content_name?: string;
  }) => {
    return trackConversion({
      event_name: 'Purchase',
      ...params,
    });
  }, [trackConversion]);

  const trackLead = useCallback((params: { 
    user_email?: string; 
    content_name?: string;
  }) => {
    return trackConversion({
      event_name: 'Lead',
      ...params,
    });
  }, [trackConversion]);

  const trackCompleteRegistration = useCallback((params: { 
    user_email?: string; 
  }) => {
    return trackConversion({
      event_name: 'CompleteRegistration',
      ...params,
    });
  }, [trackConversion]);

  const trackViewContent = useCallback((params: { 
    content_name?: string;
    content_category?: string;
  }) => {
    return trackConversion({
      event_name: 'ViewContent',
      ...params,
    });
  }, [trackConversion]);

  return {
    trackConversion,
    trackPurchase,
    trackLead,
    trackCompleteRegistration,
    trackViewContent,
  };
};
